import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { KanbanCard } from "@/types/kanban";
import { TODOS_MOTIVOS } from "@/constants/motivos-drop";
import { EyeOff, AlertCircle } from "lucide-react";

interface DropInPlaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: KanbanCard | null;
  onConfirm: (data: { motivos: string[]; observacoes?: string }) => Promise<void>;
}

export function DropInPlaceModal({
  open,
  onOpenChange,
  candidate,
  onConfirm,
}: DropInPlaceModalProps) {
  const [motivosSelecionados, setMotivosSelecionados] = useState<string[]>([]);
  const [observacoes, setObservacoes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const motivosOptions = TODOS_MOTIVOS.map((motivo) => ({
    label: motivo,
    value: motivo,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (motivosSelecionados.length === 0) return;
    
    setIsSubmitting(true);
    try {
      await onConfirm({
        motivos: motivosSelecionados,
        observacoes: observacoes.trim() || undefined,
      });
      
      // Limpar formulário
      setMotivosSelecionados([]);
      setObservacoes("");
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao ocultar candidato:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setMotivosSelecionados([]);
    setObservacoes("");
    onOpenChange(false);
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EyeOff className="h-5 w-5 text-orange-500" />
            Descartar Candidato
          </DialogTitle>
          <DialogDescription>
            O candidato <strong>{candidate.freelancer.nome}</strong> será descartado 
            e não aparecerá na visualização normal da etapa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              Motivos do descarte
              <span className="text-red-500">*</span>
            </Label>
            <MultiSelect
              options={motivosOptions}
              selected={motivosSelecionados}
              onChange={setMotivosSelecionados}
              placeholder="Selecione um ou mais motivos..."
              disabled={isSubmitting}
            />
            <p className="text-xs text-slate-500">
              Os motivos ficarão registrados no histórico de movimentações.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações adicionais (opcional)</Label>
            <Textarea
              id="observacoes"
              placeholder="Adicione detalhes ou observações relevantes..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-700">
              <p className="font-medium mb-1">Após o descarte:</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                <li>O candidato não aparecerá na visualização normal</li>
                <li>Pode ser reativado a qualquer momento</li>
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={motivosSelecionados.length === 0 || isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isSubmitting ? "Descartando..." : "Descartar Candidato"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

